<template>
  <div class="case-picker flex items-center gap-2" data-case-picker>
    <!-- Custom Dropdown -->
    <div class="flex-1 relative">
      <!-- Trigger Button -->
      <button
        @click="isOpen = !isOpen"
        class="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-left flex items-center justify-between text-sm focus:border-eggplant-400 focus:outline-hidden focus:ring-3 focus:ring-eggplant-500/10 dark:focus:border-eggplant-800 placeholder:text-gray-400 dark:placeholder:text-white/30"
      >
        <span
          :class="
            selectedCaseName
              ? 'text-gray-900 dark:text-gray-100'
              : 'text-gray-400 dark:text-gray-500'
          "
          >{{ selectedCaseName || "Select case..." }}</span
        >
        <ChevronDownIcon
          :class="['w-4 h-4 text-gray-400 transition-transform', isOpen ? 'rotate-180' : '']"
        />
      </button>

      <!-- Dropdown Content -->
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
      >
        <!-- Search Input -->
        <div class="p-3 border-b border-gray-200 dark:border-gray-600">
          <div class="relative">
            <MagnifyingGlassIcon
              class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
            />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search cases..."
              class="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            />
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 p-0.5 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <XMarkIcon class="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <!-- Case List -->
        <div class="max-h-48 overflow-y-auto">
          <!-- Loading State -->
          <div v-if="isLoading" class="p-3 text-center text-gray-500 text-sm">Loading cases...</div>

          <!-- Error State -->
          <div v-else-if="error" class="p-3 text-center text-red-500 text-sm">
            {{ error }}
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredCases.length === 0" class="p-3 text-center text-gray-500 text-sm">
            <span v-if="!props.campaignId">Select a campaign first</span>
            <span v-else-if="searchQuery">No cases found matching "{{ searchQuery }}"</span>
            <span v-else>No cases found for this campaign</span>
          </div>

          <!-- Case Items -->
          <div
            v-else
            v-for="caseItem in filteredCases"
            :key="caseItem.id"
            @click="selectCase(caseItem)"
            class="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0"
          >
            <span class="text-gray-900 dark:text-gray-100 text-sm"
              >{{ caseItem.number }} - {{ caseItem.name }}</span
            >
            <CheckIcon v-if="selectedCase === caseItem.id" class="h-4 w-4 text-green-500" />
          </div>
        </div>
      </div>
    </div>

    <!-- Action Button -->
    <button
      @click="startConversation"
      :disabled="!selectedCase"
      class="inline-flex items-center px-4 py-3 bg-eggplant-600 hover:bg-eggplant-700 border border-eggplant-600 hover:border-eggplant-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm"
    >
      Confirm selection
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

import axios from "@/api/axios";

interface CaseItem {
  id: string;
  name: string;
  number: string;
}

interface ApiCaseData {
  caseId?: string;
  caseNumber?: string;
  client?: {
    firstName?: string;
    lastName?: string;
  };
  id: string;
  plaintiffName?: string;
}

interface Props {
  campaignId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  dropdownOpen: [isOpen: boolean];
  select: [caseData: { id: string; name: string; number: string }];
}>();

const selectedCase = ref("");
const searchQuery = ref("");
const isOpen = ref(false);
const cases = ref<CaseItem[]>([]);
const isLoading = ref(false);
const error = ref<null | string>(null);

const selectedCaseName = computed(() => {
  if (!selectedCase.value) return "";
  const caseItem = cases.value.find((c) => c.id === selectedCase.value);
  return caseItem ? `${caseItem.number} - ${caseItem.name}` : "";
});

// Fetch cases from API
const fetchCases = async (campaignId: string) => {
  if (!campaignId) {
    cases.value = [];
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await axios.get(`/campaigns/${campaignId}/cases?light=true`);
    // The API returns { cases: [...], fieldDefinitions: [...] }
    const casesData = response.data.cases || [];
    cases.value = casesData.map((caseData: ApiCaseData) => ({
      id: caseData.id,
      name:
        caseData.plaintiffName ||
        (caseData.client
          ? `${caseData.client.firstName || ""} ${caseData.client.lastName || ""}`.trim()
          : "") ||
        "Unknown",
      number: caseData.caseNumber || caseData.caseId || caseData.id,
    }));
  } catch (_err) {
    error.value = "Failed to load cases";
    cases.value = [];
  } finally {
    isLoading.value = false;
  }
};

const filteredCases = computed(() => {
  if (!searchQuery.value) return cases.value;
  return cases.value.filter(
    (caseItem) =>
      caseItem.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      caseItem.number.includes(searchQuery.value)
  );
});

const selectCase = (caseItem: CaseItem) => {
  selectedCase.value = caseItem.id;
  isOpen.value = false;
  searchQuery.value = "";
};

const startConversation = () => {
  if (selectedCase.value) {
    const caseItem = cases.value.find((c) => c.id === selectedCase.value);
    if (caseItem) {
      emit("select", {
        id: caseItem.id,
        name: caseItem.name,
        number: caseItem.number,
      });
    }
  }
};

const clearSearch = () => {
  searchQuery.value = "";
};

// Global click handler to close dropdown when clicking outside
const handleGlobalClick = (event: Event) => {
  if (!isOpen.value) return;

  const target = event.target as HTMLElement;
  const isInsideComponent = target.closest("[data-case-picker]");

  if (!isInsideComponent) {
    isOpen.value = false;
  }
};

// Keyboard support
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) {
    isOpen.value = false;
  }
};

// Watch for dropdown open/close to notify parent
watch(
  () => isOpen.value,
  (newValue) => {
    // Emit dropdown state change to parent
    emit("dropdownOpen", newValue);
  }
);

// Watch for campaign ID changes
watch(
  () => props.campaignId,
  (newCampaignId) => {
    if (newCampaignId) {
      fetchCases(newCampaignId);
    } else {
      cases.value = [];
    }
    // Reset selection when campaign changes
    selectedCase.value = "";
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("click", handleGlobalClick, true);
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("click", handleGlobalClick, true);
  document.removeEventListener("keydown", handleKeydown);
});
</script>
